import {
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  TabStyles
} from '@tableau/widgets';
import { DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';
import { defaultMemoize } from 'reselect/lib';
import * as _ from 'underscore';

import { FlowOutputStepGrid } from '../../app/content/FlowOutputStepGrid';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { getHeightForRows, SortableDataGrid } from '../../common/react/components/dataGrid/SortableDataGrid';
import { classes, cssClass } from '../../style';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { ISliceable } from '../../ts/models/ISliceable';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { grey230border } from '../styles/FlowStyleConstants';

const scrollBarMaxThickness = 17;
const maxVisibleItems = 6;

const cssPrefix = 'FlowOutputsSelection';

const rootClass = cssClass(cssPrefix, 'root', {
  marginTop: TabStyles.Spacing.MediumSpace,
  marginBottom: TabStyles.Spacing.MediumSpace,
  padding: TabStyles.Spacing.SmallSpace
});

const noOutputPrompt = cssClass(cssPrefix, 'noOutputPrompt', {
  marginTop: TabStyles.Spacing.ExtraLargeSpace,
  marginBottom: TabStyles.Spacing.LargeSpace,
  color: TabStyles.Colors.AttentionDark
});

const dataGridWrapper = cssClass(cssPrefix, 'datagridWrapper', {
  boxSizing: 'border-box',
  maxHeight: getHeightForRows(maxVisibleItems) + scrollBarMaxThickness,
  marginTop: TabStyles.Spacing.MediumSpace,
  position: 'relative',
  width: '100%'
});

const gridLeftBorder = cssClass(cssPrefix, 'leftBorder', {
  width: 0,
  borderLeft: grey230border,
  marginLeft: -1,
  maxHeight: getHeightForRows(maxVisibleItems) + scrollBarMaxThickness - 1
});

export const disabledGrid = cssClass(cssPrefix, 'disabledGrid', {
  pointerEvents: 'none',
  userSelect: 'none',
  '-moz-user-select': 'none',
  '-ms-user-select': 'none',
  '-webkit-user-select': 'none'
});

function getRowData(
  outputStepInfos: ReadonlyArray<FlowOutputStepInfo>
): () => ISliceable<any> {
  const rowArray = outputStepInfos.map(outputStep => {
    return {
      ...outputStep
    };
  });
  return () => SliceableHelper.fromArray(rowArray, ServiceLocator.get<ng.IQService>('$q'));
}

function getColumns(
  selectAll: boolean,
  isCheckboxHeaderChecked: boolean,
  toggleSelectAll: () => void,
  onCheckboxClicked: (row: DataGrid.Row) => void,
  onIsIncrementalChange: (row: DataGrid.Row, isIncremental: boolean) => void,
  outputsSelected: { readonly [key: string]: boolean },
  outputsIsIncremental: { readonly [key: string]: boolean },
  isIncrementalEnabled: boolean,
  isRunAllIncremental: boolean,
): DataGrid.Column[] {
  return FlowOutputStepGrid.columns(
    isCheckboxHeaderChecked,
    toggleSelectAll,
    onCheckboxClicked,
    onIsIncrementalChange,
    outputsSelected,
    outputsIsIncremental,
    isIncrementalEnabled,
    isRunAllIncremental,
    selectAll
  );
}

export type FlowOutputsSelectionProps = {
  readonly outputs: ReadonlyArray<FlowOutputStepInfo>;
  readonly selectAll: boolean;
  readonly outputsSelected: { readonly [key: string]: boolean };
  readonly outputsIsIncremental: { readonly [key: string]: boolean };
  readonly isRunAllIncremental: boolean;
  readonly handleChildChange: (id: string) => void;
  readonly handleChildIsIncrementalChange: (id: string, isIncremental: boolean) => void;
  readonly toggleIncludeAll: () => void;
  readonly toggleSelectAll: () => void;
};

export class FlowOutputsSelection extends React.PureComponent<FlowOutputsSelectionProps> {

  private memoizedGetRowData = defaultMemoize(getRowData);
  private memoizedColumns = defaultMemoize(getColumns);


  constructor(props: FlowOutputsSelectionProps) {
    super(props);
  }

  private radioButtonOptions: RadioButtonWithLabelOption<String>[] = [
    {
      value: 'selectAll',
      testId: 'flowOutputsSelectionSelectAll',
      label: Messages.includeAllOutputStepsDetails()
    },
    {
      value: 'selectOutputs',
      testId: 'flowOutputsSelectionSelectOutputs',
      label: Messages.scheduleFlowChooseOutputMessage()
    }
  ];

  private onCheckboxClicked = (row: DataGrid.Row) => {
    this.props.handleChildChange(row.key);
  }

  private onIsIncrementalChange = (row: DataGrid.Row, isIncremental: boolean) => {
    this.props.handleChildIsIncrementalChange(row.key, isIncremental);
  }

  private handleSelect = (value: string, checkedIndex: number) => {
    if (value === 'selectAll' && !this.props.selectAll
     || value === 'selectOutputs' && this.props.selectAll) {
      this.props.toggleIncludeAll();
    }
  }

  private renderNoOutputs(): JSX.Element {
    return this.props.outputs !== null ?
      <div className={noOutputPrompt}>{Messages.flowOutputsEmptyMessage()}</div>
      : <div style={{ height: 177 }}/>; // Height of base grid to minimize resize shifting in the dialog
  }

  private renderRadioButtons(): JSX.Element {
    return (
      <RadioButtonWithLabelGroupWidget
        buttonList={this.radioButtonOptions}
        checkedIndex={this.props.selectAll ? 0 : 1}
        handleChange={this.handleSelect}
      />
    );
  }

  private renderGrid(): JSX.Element {
    const outputsSelectedCount = _.reduce(this.props.outputsSelected, (count: number, value: boolean) => {
      return value && count + 1;
    }, 0);
    const isIncrementalEnabled = !!this.props.outputs.find(output => output.isIncrementalEnabled);

    const columns = this.memoizedColumns(
      this.props.selectAll,
      outputsSelectedCount === this.props.outputs.length,
      this.props.toggleSelectAll,
      this.onCheckboxClicked,
      this.onIsIncrementalChange,
      this.props.outputsSelected,
      this.props.outputsIsIncremental,
      isIncrementalEnabled,
      this.props.isRunAllIncremental,
    );
    return (
      <div className={classes(dataGridWrapper, this.props.selectAll && disabledGrid)} style={{height: getHeightForRows(this.props.outputs.length + 1) + scrollBarMaxThickness}}>
        <div className={gridLeftBorder} style={{height: getHeightForRows(this.props.outputs.length + 1) - 1}}/>
        <SortableDataGrid
          columns={columns}
          getSliceable={this.memoizedGetRowData(this.props.outputs)}
          dataGridProps={{
            showVerticalGridLines: true
          }}
        />
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      this.props.outputs && this.props.outputs.length > 0 ?
        <div className={rootClass}>
          {this.renderRadioButtons()}
          {this.renderGrid()}
        </div>
      : this.renderNoOutputs()
    );
  }
}
